import React from "react";
import {
  GridActionsColDef,
  GridColDef,
  GridFilterInputSingleSelect,
  GridRowParams,
  getGridDateOperators,
  getGridStringOperators,
} from "@mui/x-data-grid-pro";
import formatDate from "date-fns/format";
import parseDate from "date-fns/parse";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { ACTIONS_COLUMN_ICON, ACTIONS_VIEW_ICON_SX, DataGridActionsLinkButton } from "../../../DataTableContainer";
import { DAILY_TRANSACTION_AMOUNT_COLUMN, DAILY_TRANSACTION_SERVICE_FEE_COLUMN, isAdjustment } from "../../common";
import { CurbsuiteTransactionLogEntry } from "common/graphql/queries/GetCurbsuiteTransactions";
import { getSessionEndDate, getSessionStartDate } from "../getSessionDates";
import { buildOperatorColumn, buildTransactionDateColumn, TRANSACTION_INDEX_COLUMN } from "./sharedColumns";
import { BUS_STATION_TRANSACTION_TYPE_LABELS } from "./busStationTransactionTypeLabels";
import {
  buildActionsColumnWithAdjustment,
  buildAdjustmentReferenceActions,
  renderTypeCellWithAdjustment,
} from "./adjustmentColumns";

const PARK_PERIOD_FORMAT = "hh:mm aa";

/**
 * Columns for the "park" variant: regular payees, bus-station-park (Philly Bus), and airport-park
 * (GSP trip charges). All three share this shape; `isBusStation`/`isAirportTripCharges` toggle the
 * pieces specific to one of them.
 *
 * Only ever called for variant === "park", so "bus station" and "bus-station-park" are the same
 * condition here -- `isBusStation` alone is enough, no separate isBusStationPark flag needed.
 */
export function buildParkColumns({
  isBusStation,
  isAirportTripCharges,
  showsOperatorBilling,
  showsServiceFee,
  searchParams,
}: {
  isBusStation: boolean;
  isAirportTripCharges: boolean;
  showsOperatorBilling: boolean;
  /** Includes the Service Fee column (AUT-6175: `display_service_fee_column` flag). */
  showsServiceFee: boolean;
  /** The page's current search params, which each row's "View Transaction" link extends with `transactionId`. */
  searchParams: URLSearchParams;
}): GridColDef[] {
  const transactionAssignedDateColumns: GridColDef[] = isBusStation
    ? [
        {
          field: "transactionAssignedDate",
          headerName: "Date",
          type: "date",
          valueGetter({ row }) {
            return row.assignedToAccountAtLocalDate
              ? parseDate(row.assignedToAccountAtLocalDate, "yyyy-MM-dd", new Date())
              : null;
          },
          valueFormatter: (params) => (params.value ? formatDate(params.value, "MM/dd/yyyy") : ""),
          filterable: true,
          sortable: true,
          filterOperators: getGridDateOperators().filter((op) => ["is", "before", "after"].includes(op.value)),
        } as GridColDef<CurbsuiteTransactionLogEntry, Date | null, string>,
      ]
    : [];

  // A trip charge is an instant (one LPR entry capture), not a session -- no session_ended_at, so
  // getSessionEndDate falls back to the epoch. Airports OMIT this column rather than hiding it via
  // defaultVisibilityModel: a hidden column is still in the column list, and buildExportColumns
  // would expand it into Event Start/End + Duration in the CSV.
  const timeRangeColumns: GridColDef[] = isAirportTripCharges
    ? []
    : [
        {
          field: "timeRange",
          headerName: "Session Period",
          // Business-logic-hidden (via defaultVisibilityModel), not user-toggleable, for
          // bus-station "park" accounts.
          hideable: !isBusStation,
          sortComparator: (v1, v2) => (v1?.end.getTime() ?? 0) - (v2?.end.getTime() ?? 0),
          flex: 1,
          align: "left",
          headerAlign: "left",
          sortingOrder: ["desc", "asc"],
          valueGetter({ row }) {
            // An adjustment is a ledger correction, not a session: no period to show (AUT-6163).
            if (isAdjustment(row)) {
              return null;
            }
            return {
              start: getSessionStartDate(row),
              end: getSessionEndDate(row),
            };
          },
          valueFormatter: (params) =>
            params.value
              ? `${formatDate(params.value.start, PARK_PERIOD_FORMAT)} - ${formatDate(
                  params.value.end,
                  PARK_PERIOD_FORMAT,
                )}`
              : "",
          filterable: false,
        } as GridColDef<CurbsuiteTransactionLogEntry, { start: Date; end: Date } | null, string>,
      ];

  const busStationTransactionTypeColumns: GridColDef[] = isBusStation
    ? [
        {
          field: "busStationTransactionType",
          headerName: "Type",
          type: "singleSelect",
          valueOptions: [
            {
              value: BUS_STATION_TRANSACTION_TYPE_LABELS.park,
              label: BUS_STATION_TRANSACTION_TYPE_LABELS.park,
            },
            {
              value: BUS_STATION_TRANSACTION_TYPE_LABELS.gateway_transaction,
              label: BUS_STATION_TRANSACTION_TYPE_LABELS.gateway_transaction,
            },
            {
              value: BUS_STATION_TRANSACTION_TYPE_LABELS.adjustment,
              label: BUS_STATION_TRANSACTION_TYPE_LABELS.adjustment,
            },
          ],
          sortable: false,
          filterable: true,
          flex: 1,
          valueGetter({ row }: { row: CurbsuiteTransactionLogEntry }) {
            if (row.transactionType === "park") {
              return BUS_STATION_TRANSACTION_TYPE_LABELS.park;
            }
            if (row.transactionType === "gateway_transaction") {
              return BUS_STATION_TRANSACTION_TYPE_LABELS.gateway_transaction;
            }
            // The cell value stays the bare label so the singleSelect "equals" filter matches it;
            // renderCell adds the reason and the "Adjusts #" link under it (AUT-6163).
            if (isAdjustment(row)) {
              return BUS_STATION_TRANSACTION_TYPE_LABELS.adjustment;
            }
            return "";
          },
          renderCell: renderTypeCellWithAdjustment(searchParams, { isBusStationPark: true }),
          filterOperators: getGridStringOperators()
            .filter((op) => op.value === "equals")
            .map((operator) => ({
              ...operator,
              InputComponent: operator.InputComponent ? GridFilterInputSingleSelect : undefined,
            })),
        } as GridColDef<CurbsuiteTransactionLogEntry, string>,
      ]
    : [];

  const getParkActions = (params: GridRowParams<CurbsuiteTransactionLogEntry>): JSX.Element[] => {
    // An adjustment links to the charge it adjusts (a park's detail page, or this table filtered to a
    // permit charge's ID and month) rather than to a detail page of its own.
    if (isAdjustment(params.row)) {
      return buildAdjustmentReferenceActions(params.row, searchParams, { isBusStationPark: isBusStation });
    }
    if (params.row.transactionType === "gateway_transaction") {
      return [];
    }
    // Copy rather than mutate `searchParams` — this runs once per row on every render, and
    // mutating the shared object would leak each row's transactionId into the next.
    const transactionSearchParams = new URLSearchParams(searchParams);
    transactionSearchParams.set("transactionId", params.row.transactionId);
    return [
      <DataGridActionsLinkButton
        to={`?${transactionSearchParams.toString()}`}
        replace
        label={"View Transaction"}
        icon={<ArrowForwardIosIcon sx={ACTIONS_VIEW_ICON_SX} />}
      />,
    ];
  };

  const operatorBillingColumns: GridColDef[] = showsOperatorBilling
    ? [buildOperatorColumn(), ...busStationTransactionTypeColumns]
    : [];

  return [
    TRANSACTION_INDEX_COLUMN,
    ...transactionAssignedDateColumns,
    buildTransactionDateColumn({ isAirportWallet: false, isBusStationPark: isBusStation, showsOperatorBilling }),
    ...timeRangeColumns,
    ...operatorBillingColumns,
    {
      field: "licensePlate",
      headerName: showsOperatorBilling ? "Plate" : "License Plate",
      align: "left",
      headerAlign: "left",
      type: "string",
      sortable: false,
      flex: 1,
      valueGetter({ row }) {
        const vehicle = row.transaction.registeredVehicle;
        return vehicle ? `${vehicle.stateCode} ${vehicle.licensePlate}` : "-";
      },
      filterable: true,
      filterOperators: getGridStringOperators().filter((operator) => ["equals", "contains"].includes(operator.value)),
    } as GridColDef<CurbsuiteTransactionLogEntry, string, string>,
    {
      field: "addressStreet",
      headerName: "Address",
      align: "left",
      headerAlign: "left",
      type: "string",
      flex: 1,
      sortable: false,
      valueGetter({ row }) {
        return row.addressStreet;
      },
      filterable: false,
    } as GridColDef<CurbsuiteTransactionLogEntry, string>,
    DAILY_TRANSACTION_AMOUNT_COLUMN,
    // Airports (GSP) OMIT Service Fee rather than hide it, for the same reason as timeRange above:
    // a hidden column would still reach buildExportColumns. Their Transactions-tab rows are
    // trip_charge rows, which never carry a card-payment service fee (it applies only to
    // wallet_reload/permit charges), and the airport export's field list is product-specified
    // (see EXPORT_COLUMNS in CurbPassTransactionsTable.tsx) -- an always-$0.00 column there would
    // be noise. Everyone else gets it only behind the `display_service_fee_column` flag (AUT-6175).
    ...(showsServiceFee && !isAirportTripCharges ? [DAILY_TRANSACTION_SERVICE_FEE_COLUMN] : []),
    // The standard park tab has no Type column, so an adjustment row's two-line Type cell (AUT-6163)
    // goes in its actions column instead of a new column. Bus stations show it in their Type
    // column; airports' trip charges can't be adjusted.
    isBusStation || isAirportTripCharges
      ? ({ ...ACTIONS_COLUMN_ICON, getActions: getParkActions } as GridActionsColDef)
      : buildActionsColumnWithAdjustment({ searchParams, getActions: getParkActions }),
  ];
}
